import forge from 'apis/forge';
import SceneConversation from 'components/Panel/Chat/Conversation/SceneConversation';
import FrameGallery from 'components/FrameGallery';
import Icon, { IconType } from 'components/Icon';
import ShareSceneModal from 'components/Modal/Share/ShareSceneModal';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { CHAT_COLUMN_GAP, ChatStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveSceneChat, selectActiveScene, selectSceneChatRefs } from 'redux/db/reducers';
import { canEdit, getRightmostFrameX } from 'redux/db/util';
import { setActiveSceneChatId, setCenteredFrameId, setSidebarVisible } from 'redux/mode/actions';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { logout } from 'utils';
import { v4 as uuid } from 'uuid';
import * as S from './styles';

const WorkspaceMobile = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [showShareModal, setShowShareModal] = useState(false);

	const { activeSceneChatId, activeScene, chatRefs, activeChat, centeredFrameId } = useSelector(
		(r: RootReducer) => {
			return {
				activeSceneChatId: r.mode.activeSceneChatId,
				activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
				chatRefs: selectSceneChatRefs(r.db),
				activeChat: selectActiveSceneChat(r.db, r.mode.activeSceneChatId),
				centeredFrameId: r.mode.centeredFrameId,
			};
		},
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewChat = useCallback(async () => {
		if (!activeScene) return;
		if (!canEdit(activeScene)) {
			displayViewOnlyModal();
			return;
		}

		const chatId = uuid();
		const rightmostFrameX = getRightmostFrameX();

		await forge.createSceneChat({
			id: chatId,
			sceneId: activeScene.id,
			title: NEW_CHAT_TITLE,
			x: rightmostFrameX !== null ? rightmostFrameX + CHAT_COLUMN_GAP : 0,
			y: 0,
			metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
		});

		dispatch(setActiveSceneChatId(chatId));
	}, [activeScene, activeSceneChatId, dispatch]);

	// Chat refs are already sorted by updatedAt DESC from the backend
	const sortedChatRefs = useMemo(() => {
		return chatRefs;
	}, [chatRefs]);

	// Auto-select first chat if no active chat is set (same logic as Chats component)
	useEffect(() => {
		if (!activeSceneChatId || !sortedChatRefs.some((ref) => ref.id === activeSceneChatId)) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveSceneChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, activeSceneChatId, dispatch]);

	// Create menu items for chats dropdown
	const chatMenuItems = useMemo(() => {
		const items = [
			new MenuItemAction('Create new chat...', '', () => {
				onNewChat();
			}),
			new MenuItemDivider(),
		];

		if (sortedChatRefs.length > 0) {
			items.push(
				...sortedChatRefs.map((ref) => {
					const isActive = ref.id === activeSceneChatId;
					return new MenuItemAction(
						ref.title || 'Untitled Chat',
						'',
						() => {
							dispatch(setActiveSceneChatId(ref.id));
							dispatch(setSidebarVisible(false)); // Close sidebar when chat is selected
						},
						false,
						isActive // showCheckmark
					);
				})
			);
		}

		return items;
	}, [sortedChatRefs, activeSceneChatId, dispatch, onNewChat]);

	const moreMenuItems = [
		MenuItemAction.createReturnToProjectList(),
		new MenuItemAction('Log out', '', () => {
			logout();
		}),
		...(activeSceneChatId && canEdit(activeScene)
			? [MenuItemAction.createRenameChat(activeSceneChatId)]
			: []),
	];

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base>
			<S.Application>
				<S.Header>
					<S.HeaderLeft>
						<SidebarToggle />
						{activeChat && (
							<DropdownMenu menuItems={chatMenuItems}>
								<S.ChatTitleButton>
									<S.ChatTitle>{activeChat.title || 'Untitled Chat'}</S.ChatTitle>
									<S.ChatTitleIcon>
										<Icon
											type={IconType.FILLED_ARROW_DOWN}
											width={16}
											height={16}
											color={theme.gray[800]}
										/>
									</S.ChatTitleIcon>
								</S.ChatTitleButton>
							</DropdownMenu>
						)}
					</S.HeaderLeft>
					<S.HeaderRight>
						<S.HeaderButton onClick={() => setShowShareModal(true)}>
							<Icon type={IconType.SHARE} width={24} height={24} color={theme.gray[1000]} />
						</S.HeaderButton>
						<DropdownMenu menuItems={moreMenuItems}>
							<S.MoreButton>
								<S.InnerWrapper>
									<S.MoreButtonLogo>
										<Icon type={IconType.MORE2} width={24} height={24} color={theme.gray[1000]} />
									</S.MoreButtonLogo>
								</S.InnerWrapper>
							</S.MoreButton>
						</DropdownMenu>
					</S.HeaderRight>
				</S.Header>

				<S.MainContent>
					{activeSceneChatId ? (
						<SceneConversation />
					) : (
						<S.Placeholder>
							<S.PlaceholderIcon>
								<Icon type={IconType.CHAT} width={48} height={48} color={theme.gray[400]} />
							</S.PlaceholderIcon>
							<S.PlaceholderTitle>Start a new conversation</S.PlaceholderTitle>
							<S.PlaceholderDescription>
								Tap "New Chat" to begin designing with AI
							</S.PlaceholderDescription>
						</S.Placeholder>
					)}
				</S.MainContent>
			</S.Application>

			{centeredFrameId && (
				<FrameGallery
					frameId={centeredFrameId}
					sceneControls={true}
					onClose={() => dispatch(setCenteredFrameId(null))}
				/>
			)}
			{showShareModal && activeScene && (
				<ShareSceneModal scene={activeScene} onClose={() => setShowShareModal(false)} />
			)}
		</S.Base>
	);
};

export default WorkspaceMobile;
